import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ShoppingCart, User, LogOut, Settings, ChevronDown, Menu, X, LayoutDashboard, Package, Truck, Users, Trophy, Video, Mail, Phone, Heart, HelpCircle, Home } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { LanguageSwitcher } from './LanguageSwitcher'
import { useLanguage } from '@/lib/context/LanguageContext'
import { NotificationCenter } from '@/components/notifications/NotificationCenter'

const ADMIN_EMAILS = ['509aem@gmail.com', 'aemfamily509@gmail.com']

export function Navbar() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()
  const user = useAuthStore((state) => state.user)
  const signOut = useAuthStore((state) => state.signOut)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [sellerStatus, setSellerStatus] = useState<string | null>(null)
  const [deliveryStatus, setDeliveryStatus] = useState<string | null>(null)
  const { currentLanguage, changeLanguage } = useLanguage()

  const isAdmin = user?.email && ADMIN_EMAILS.includes(user.email)
  const isManager = user?.role === 'manager'
  const isHomePage = location.pathname === '/'

  useEffect(() => {
    if (user) {
      checkSellerStatus()
      checkDeliveryStatus()
    }
  }, [user])

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  const checkSellerStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('seller_stores')
        .select('status')
        .eq('seller_id', user?.id)
        .maybeSingle()

      if (error) throw error
      setSellerStatus(data?.status || null)
    } catch (err) {
      console.error('Error checking seller status:', err)
    }
  }

  const checkDeliveryStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('delivery_partners')
        .select('status')
        .eq('id', user?.id)
        .maybeSingle()

      if (error) throw error
      setDeliveryStatus(data?.status || null)
    } catch (err) {
      console.error('Error checking delivery status:', err)
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (error) {
      console.error('Error signing out:', error)
    }
  }

  // Get seller navigation link
  const getSellerLink = () => {
    if (sellerStatus === 'approved') {
      return {
        to: "/seller/dashboard",
        text: t('nav.sellerDashboard')
      }
    } else if (sellerStatus === 'pending' || sellerStatus === 'rejected') {
      return {
        to: "/seller/application-status",
        text: "Statut de la Demande"
      }
    } else {
      return {
        to: "/seller/register",
        text: t('nav.becomeSeller')
      }
    }
  }

  // Get delivery navigation link
  const getDeliveryLink = () => {
    if (deliveryStatus === 'active') {
      return {
        to: "/delivery/dashboard",
        text: t('nav.deliveryDashboard')
      }
    } else if (deliveryStatus === 'pending' || deliveryStatus === 'rejected') {
      return {
        to: "/delivery/application-status",
        text: "Statut de la Demande"
      }
    } else {
      return {
        to: "/delivery/register",
        text: t('nav.becomeDelivery')
      }
    }
  }

  const sellerLink = getSellerLink()
  const deliveryLink = getDeliveryLink()

  return (
    <nav className="bg-primary shadow-md fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="text-2xl font-bold text-white">
              AEM
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:ml-6 md:flex md:space-x-4">
              {!isHomePage && (
                <Link
                  to="/"
                  className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                >
                  <Home className="h-4 w-4 inline mr-2" />
                  Accueil
                </Link>
              )}
              <Link
                to="/products"
                className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
              >
                {t('nav.products')}
              </Link>
              <Link
                to="/videos"
                className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
              >
                {t('nav.videos')}
              </Link>
              {!isAdmin && (
                <>
                  {(!isManager || user?.role === 'seller') && (
                    <Link
                      to={sellerLink.to}
                      className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                    >
                      {sellerLink.text}
                    </Link>
                  )}
                  {(!isManager || user?.role === 'delivery') && (
                    <Link
                      to={deliveryLink.to}
                      className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                    >
                      {deliveryLink.text}
                    </Link>
                  )}
                  {isManager && user?.role === 'manager' && (
                    <Link
                      to="/management"
                      className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                    >
                      <LayoutDashboard className="h-4 w-4 inline mr-2" />
                      {t('nav.management')}
                    </Link>
                  )}
                </>
              )}
              {isAdmin && (
                <>
                  <Link
                    to="/admin/orders"
                    className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                  >
                    <Package className="h-4 w-4 inline mr-2" />
                    {t('nav.orders')}
                  </Link>
                  <Link
                    to="/admin/delivery-partners"
                    className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                  >
                    <Users className="h-4 w-4 inline mr-2" />
                    {t('nav.deliveryPartners')}
                  </Link>
                  <Link
                    to="/admin/deliveries"
                    className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                  >
                    <Truck className="h-4 w-4 inline mr-2" />
                    {t('nav.deliveries')}
                  </Link>
                  <Link
                    to="/admin/videos"
                    className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
                  >
                    <Video className="h-4 w-4 inline mr-2" />
                    {t('nav.videos')}
                  </Link>
                </>
              )}
              <Link
                to="/contact"
                className="px-4 py-2 text-sm font-medium text-white hover:bg-primary-dark rounded-full transition-colors"
              >
                Contact
              </Link>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="flex items-center space-x-2 md:hidden">
            {user && <NotificationCenter />}
            <Link to="/cart" className="p-2">
              <ShoppingCart className="h-6 w-6 text-white" />
            </Link>
            <button
              type="button"
              className="mobile-menu-button p-2 rounded-md text-white hover:bg-primary-dark focus:outline-none"
              onClick={(e) => {
                e.stopPropagation()
                setIsMobileMenuOpen(!isMobileMenuOpen)
              }}
              aria-expanded={isMobileMenuOpen}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              <span className="sr-only">Open menu</span>
              {isMobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>

          {/* Desktop right navigation */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            <LanguageSwitcher />
            
            {user && <NotificationCenter />}
            
            <Link to="/cart">
              <Button variant="ghost" size="sm" className="text-white hover:bg-primary-dark">
                <ShoppingCart className="h-5 w-5" />
              </Button>
            </Link>
            
            {user ? (
              <div className="relative user-menu">
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-primary-dark flex items-center space-x-2"
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  aria-expanded={showUserMenu}
                  aria-haspopup="true"
                >
                  {user.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={user.full_name || 'Profile'}
                      className="h-6 w-6 rounded-full"
                    />
                  ) : (
                    <User className="h-5 w-5" />
                  )}
                  <ChevronDown className={`h-4 w-4 transition-transform ${
                    showUserMenu ? 'rotate-180' : ''
                  }`} />
                </Button>

                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                      {user.email}
                    </div>
                    {isAdmin && (
                      <>
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <LayoutDashboard className="h-4 w-4 inline mr-2" />
                          {t('nav.admin')}
                        </Link>
                        <Link
                          to="/admin/competitions"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Trophy className="h-4 w-4 inline mr-2" />
                          {t('nav.competitions')}
                        </Link>
                        <Link
                          to="/admin/videos"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Video className="h-4 w-4 inline mr-2" />
                          Gérer les Vidéos
                        </Link>
                      </>
                    )}
                    {isManager && user?.role === 'manager' && (
                      <>
                        <Link
                          to="/management"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <LayoutDashboard className="h-4 w-4 inline mr-2" />
                          {t('nav.management')}
                        </Link>
                        <Link
                          to="/management/competitions"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowUserMenu(false)}
                        >
                          <Trophy className="h-4 w-4 inline mr-2" />
                          {t('nav.competitions')}
                        </Link>
                      </>
                    )}
                    {user?.role === 'delivery' && deliveryStatus === 'active' && (
                      <Link
                        to="/delivery/competitions"
                        className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setShowUserMenu(false)}
                      >
                        <Trophy className="h-4 w-4 inline mr-2" />
                        {t('nav.competitions')}
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User className="h-4 w-4 inline mr-2" />
                      {t('nav.profile')}
                    </Link>
                    <Link
                      to="/thanks"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Heart className="h-4 w-4 inline mr-2" />
                      Remerciements
                    </Link>
                    <Link
                      to="/faqs"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <HelpCircle className="h-4 w-4 inline mr-2" />
                      FAQs
                    </Link>
                    <Link
                      to="/settings"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Settings className="h-4 w-4 inline mr-2" />
                      {t('nav.settings')}
                    </Link>
                    <Link
                      to="/privacy"
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Settings className="h-4 w-4 inline mr-2" />
                      Confidentialité
                    </Link>
                    <button
                      onClick={() => {
                        setShowUserMenu(false)
                        handleSignOut()
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                    >
                      <LogOut className="h-4 w-4 inline mr-2" />
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login">
                <Button variant="secondary" size="sm" className="bg-white text-primary hover:bg-gray-100">
                  <User className="h-5 w-5 mr-2" />
                  {t('nav.login')}
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div 
        className={`fixed inset-0 top-16 bg-primary z-40 transition-transform duration-300 ease-in-out transform ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        aria-hidden={!isMobileMenuOpen}
      >
        <div className="h-full overflow-y-auto pb-20">
          <div className="px-4 py-6 space-y-6">
            <div className="space-y-4">
              {!isHomePage && (
                <Link
                  to="/"
                  className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Home className="h-5 w-5 inline mr-3" />
                  Accueil
                </Link>
              )}
              <Link
                to="/products"
                className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.products')}
              </Link>
              <Link
                to="/videos"
                className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('nav.videos')}
              </Link>
              {!isAdmin && (
                <>
                  {(!isManager || user?.role === 'seller') && (
                    <Link
                      to={sellerLink.to}
                      className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {sellerLink.text}
                    </Link>
                  )}
                  {(!isManager || user?.role === 'delivery') && (
                    <Link
                      to={deliveryLink.to}
                      className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      {deliveryLink.text}
                    </Link>
                  )}
                  {isManager && user?.role === 'manager' && (
                    <>
                      <Link
                        to="/management"
                        className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <LayoutDashboard className="h-5 w-5 inline mr-3" />
                        {t('nav.management')}
                      </Link>
                      <Link
                        to="/management/competitions"
                        className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Trophy className="h-5 w-5 inline mr-3" />
                        {t('nav.competitions')}
                      </Link>
                    </>
                  )}
                  {user?.role === 'delivery' && deliveryStatus === 'active' && (
                    <Link
                      to="/delivery/competitions"
                      className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <Trophy className="h-5 w-5 inline mr-3" />
                      {t('nav.competitions')}
                    </Link>
                  )}
                </>
              )}
              {isAdmin && (
                <>
                  <Link
                    to="/admin/orders"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Package className="h-5 w-5 inline mr-3" />
                    {t('nav.orders')}
                  </Link>
                  <Link
                    to="/admin/delivery-partners"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Users className="h-5 w-5 inline mr-3" />
                    {t('nav.deliveryPartners')}
                  </Link>
                  <Link
                    to="/admin/deliveries"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Truck className="h-5 w-5 inline mr-3" />
                    {t('nav.deliveries')}
                  </Link>
                  <Link
                    to="/admin/competitions"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Trophy className="h-5 w-5 inline mr-3" />
                    {t('nav.competitions')}
                  </Link>
                  <Link
                    to="/admin/videos"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Video className="h-5 w-5 inline mr-3" />
                    {t('nav.videos')}
                  </Link>
                </>
              )}
              <Link
                to="/faqs"
                className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <HelpCircle className="h-5 w-5 inline mr-3" />
                FAQs
              </Link>
              <Link
                to="/contact"
                className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Mail className="h-5 w-5 inline mr-3" />
                Contact
              </Link>
              <Link
                to="/privacy"
                className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Settings className="h-5 w-5 inline mr-3" />
                Confidentialité
              </Link>
            </div>

            <div className="pt-6 border-t border-primary-dark">
              {user ? (
                <div className="space-y-4">
                  <div className="px-4 py-3 text-lg font-medium text-white">
                    {user.email}
                  </div>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="h-5 w-5 inline mr-3" />
                      {t('nav.admin')}
                    </Link>
                  )}
                  {isManager && user?.role === 'manager' && (
                    <Link
                      to="/management"
                      className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="h-5 w-5 inline mr-3" />
                      {t('nav.management')}
                    </Link>
                  )}
                  <Link
                    to="/profile"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <User className="h-5 w-5 inline mr-3" />
                    {t('nav.profile')}
                  </Link>
                  <Link
                    to="/thanks"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Heart className="h-5 w-5 inline mr-3" />
                    Remerciements
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-3 text-lg font-medium text-white hover:bg-primary-dark rounded-lg transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <Settings className="h-5 w-5 inline mr-3" />
                    {t('nav.settings')}
                  </Link>
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false)
                      handleSignOut()
                    }}
                    className="block w-full text-left px-4 py-3 text-lg font-medium text-red-300 hover:bg-primary-dark rounded-lg transition-colors"
                  >
                    <LogOut className="h-5 w-5 inline mr-3" />
                    {t('nav.logout')}
                  </button>
                </div>
              ) : (
                <div className="px-4">
                  <Link
                    to="/login"
                    className="block w-full text-center px-6 py-3 text-lg font-medium text-primary bg-white rounded-lg hover:bg-gray-50 border-primary md:py-4 md:text-lg md:px-10 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <User className="h-5 w-5 inline mr-3" />
                    {t('nav.login')}
                  </Link>
                </div>
              )}
              
              {/* Mobile Language Switcher */}
              <div className="mt-6 px-4">
                <p className="text-white/80 text-sm mb-2">Langue / Language / Lang</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { code: 'fr', name: 'Français' },
                    { code: 'en', name: 'English' },
                    { code: 'ht', name: 'Kreyòl' }
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code as 'fr' | 'en' | 'ht');
                        setIsMobileMenuOpen(false);
                      }}
                      className={`py-2 px-3 text-sm rounded-md ${
                        currentLanguage === lang.code
                          ? 'bg-white text-primary font-medium'
                          : 'bg-primary-dark text-white'
                      }`}
                    >
                      {lang.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  )
}